<script lang="ts" setup>
    import { onMounted, ref, nextTick } from 'vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ActionButton from "../../components/ActionButton.vue"; 
    import MessageSection from '../../components/Messages/MessageSection.vue'
    import { ICol, ValueStrDelegate } from "../../lib/ChoiceTableHelpers";
    import { ccdaBuilder } from '../../lib/ChoiceApi';
    import { CCDABuildLocation, CCDABuildStatus } from "../../Enums/ChoiceEnums";
    import { TableRowType, MessageSectionType, DisplayStyle } from "../../Enums/UiEnums";
    import { ICCDASummaryItem } from '../../lib/Models/CCDAModel';

    const now = new Date();
    const startDate = ref<string>(now.toLocaleDateString());
    const stopDate = ref<string>(now.toLocaleDateString());
    const tableData = ref<ICCDASummaryItem[]>([]);
    const searching = ref<boolean>(false);
    const displayErrorMessage = ref<string>("");

    const FormatName: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
             return row?.patient_name + 
                (row?.birth_date.length > 0 ? " " + row?.birth_date + " (" + row?.age + ")" : "") + 
                " " + row?.gender;
        }
    }

    const FormatStatus: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
             return CCDABuildStatus[row?.status];
        }
    }

    const tableColumns = ref<ICol[]>([
        { Name: 'Identifier', DataField: 'record_id' },
        { Name: 'Chart #', DataField: 'patient_id' },
        { Name: 'Patient', DataField: 'patient_name', FormatValue: FormatName },
        { Name: 'Status', DataField: 'status', FormatValue: FormatStatus },
        { Name: 'Encounter', DataField: 'encounter_date' },
        { Name: 'Send', DataField: 'send_date' },
        { Name: 'Built', DataField: 'build_date' },
        { Name: 'Sent', DataField: 'sent_date' },
        { Name: 'Status', DataField: 'status_date' },
    ]);

    onMounted(async () => {
        LoadFHIRSummary();
    });

    async function LoadFHIRSummary() {
        displayErrorMessage.value = "";
        if (startDate.value.length == 10 && stopDate.value.length == 10) {
            if (Date.parse(startDate.value) > Date.parse(stopDate.value)) {
                displayErrorMessage.value = "Error: Start Date cannot be greater than the Stop Date";
                setTimeout(() => searching.value = false, 250);
                return;
            }
        }
        const post_data = {
            sendLocationId: CCDABuildLocation.FHIR,
            startDate: startDate.value,
            stopDate: stopDate.value
        }
        searching.value = true;
        const resp = await ccdaBuilder.Summary(post_data);
        searching.value = false;
        tableData.value = resp.summaryList;
    }
</script>

<style>
    .fixedLabelWidth {
        width: 40px;
    }
    .fixedDateWidth {
        width: 180px;
    }
    .fixedButtonWidth {
        width: 180px;
    }
</style>

<template>
    <div class="row">
        <div class="fixedLabelWidth mt-2">
            Start
        </div>
        <div class="fixedDateWidth">
            <DatePickerNoTZ v-model:value="startDate" :clearable="false" />
        </div>
        <div class="fixedLabelWidth mt-2">
            Stop
        </div>
        <div class="fixedDateWidth">
            <DatePickerNoTZ v-model:value="stopDate" :clearable="false" />
        </div>
        <div class="fixedDateWidth">
            <ActionButton
                baseText = "Search"
                spinText = "Searching"
                :executing="searching"    
                :disabled="searching"   
                :clickEvent="LoadFHIRSummary" 
                :toolTip="'Perform Search'"
                :width="125">
            </ActionButton>             
        </div>
        <div class="col mt-2">
            <MessageSection v-if="displayErrorMessage" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{displayErrorMessage}}
            </MessageSection>    
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <p style="font-size: 0.75rem;">
                <ChoiceTable 
                    ref="testTable"
                    :columns="tableColumns" 
                    :data="tableData" 
                    :show-filters="false" 
                    :allowSorting="true"
                    :key-value-data-member="'Col3Val'">Search
                </ChoiceTable>
            </p>
        </div>
    </div>
</template>

